import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../contexts/AuthContext';
import { 
  Calendar, 
  Search, 
  Plus,
  Edit,
  Trash2,
  Eye,
  RefreshCw,
  X,
  Clock,
  AlertTriangle
} from 'lucide-react';
import moment from 'moment';
import toast from 'react-hot-toast';

const Appointments = () => {
  const { user } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [users, setUsers] = useState([]);
  // Reschedule functionality state (for parents)
  const [rescheduleLimit, setRescheduleLimit] = useState(null);
  const [rescheduleOptions, setRescheduleOptions] = useState([]);
  const [selectedRescheduleOption, setSelectedRescheduleOption] = useState(null);
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [loadingRescheduleOptions, setLoadingRescheduleOptions] = useState(false);
  // Cancellation limit state (for parents)
  const [cancellationLimit, setCancellationLimit] = useState(null);

  useEffect(() => {
    fetchAppointments();
    if (user?.role === 'admin') {
      fetchUsers();
    }
  }, [currentPage, searchTerm, statusFilter, dateFilter, user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh appointments periodically to reflect chronological status updates
  useEffect(() => {
    // Refresh every 2 minutes to update appointment statuses (in-progress, completed, etc.)
    const refreshInterval = setInterval(() => {
      fetchAppointments();
    }, 2 * 60 * 1000); // 2 minutes

    // Cleanup interval on unmount
    return () => clearInterval(refreshInterval);
  }, [currentPage, searchTerm, statusFilter, dateFilter, user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      let response;
      
      if (user?.role === 'teacher') {
        // Teachers can only view their assigned appointments
        const params = {
          ...(statusFilter && { status: statusFilter }),
          ...(dateFilter && { startDate: dateFilter })
        };
        response = await api.get('/appointments/teacher', { params });
        setAppointments(response.data.appointments || []);
        setTotalPages(1);
      } else if (user?.role === 'parent') {
        // Parents can only view their booked appointments
        const params = {
          page: currentPage,
          limit: 10,
          ...(searchTerm && { search: searchTerm }),
          ...(statusFilter && { status: statusFilter }),
          ...(dateFilter && { startDate: dateFilter })
        };
        response = await api.get('/appointments', { params });
        setAppointments(response.data.appointments || []);
        setTotalPages(response.data.pagination?.totalPages || 0);
      } else if (user?.role === 'admin') {
        // Admins can view all appointments with full management
        const params = {
          page: currentPage,
          limit: 10,
          ...(searchTerm && { search: searchTerm }),
          ...(statusFilter && { status: statusFilter }),
          ...(dateFilter && { startDate: dateFilter })
        };
        response = await api.get('/appointments', { params });
        setAppointments(response.data.appointments || []);
        setTotalPages(response.data.pagination?.totalPages || 0);
      }
    } catch (error) {
      console.error('Error fetching appointments:', error);
      
      // Only show error toast for actual errors, not empty responses
      if (error.response?.status >= 400) {
        toast.error('Failed to fetch appointments');
      } else {
        // For network errors or other issues, still show error but don't treat as critical
        console.warn('Non-critical error fetching appointments:', error.message);
      }
      
      // Ensure we have empty state on any error
      setAppointments([]);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data.users);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleDeleteAppointment = async (appointmentId) => {
    if (!window.confirm('Are you sure you want to cancel this appointment?')) {
      return;
    }

    try {
      await api.delete(`/appointments/${appointmentId}`);
      toast.success('Appointment cancelled successfully');
      fetchAppointments();
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      toast.error('Failed to cancel appointment');
    }
  };

  const handleViewAppointment = (appointment) => {
    setSelectedAppointment(appointment);
    setShowViewModal(true);
  };

  const handleEditAppointment = (appointment) => {
    setSelectedAppointment(appointment);
    setEditFormData({
      student: appointment.student?._id || '',
      teacher: appointment.teacher?._id || '',
      subject: appointment.subject || '',
      scheduledDate: appointment.scheduledDate ? moment(appointment.scheduledDate).format('YYYY-MM-DD') : '',
      startTime: appointment.startTime || '',
      endTime: appointment.endTime || '',
      notes: appointment.notes || ''
    });
    setShowEditModal(true);
  };


  const handleUpdateAppointment = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/appointments/${selectedAppointment._id}`, editFormData);
      toast.success('Appointment updated successfully');
      setShowEditModal(false);
      setSelectedAppointment(null);
      fetchAppointments();
    } catch (error) {
      console.error('Error updating appointment:', error);
      toast.error('Failed to update appointment');
    }
  };

  const [bookingInProgress, setBookingInProgress] = useState(false);

  const handleBookAppointment = async (appointmentId) => {
    if (bookingInProgress) return;

    if (!window.confirm('Are you sure you want to book this appointment?')) {
      return;
    }

    try {
      setBookingInProgress(true);
      // For now, we'll use the first student of the parent
      // In a real app, you'd have a student selection modal
      const response = await api.get('/students');
      const students = response.data.students;
      
      if (students.length === 0) {
        toast.error('No students found. Please add a student first.');
        return;
      }

      await api.post('/appointments/book', {
        appointmentId: appointmentId,
        student: students[0]._id,
        notes: 'Booked by parent'
      });
      
      toast.success('Appointment booked successfully!');
      fetchAppointments();
    } catch (error) {
      console.error('Error booking appointment:', error);
      toast.error(error.response?.data?.message || 'Failed to book appointment');
    } finally {
      setBookingInProgress(false);
    }
  };


  const handleCancelAppointment = async (appointment) => {
    setSelectedAppointment(appointment);
    
    // Check cancellation limit for parents
    if (user?.role === 'parent') {
      try {
        const response = await api.get('/appointments/cancellation-limit');
        setCancellationLimit(response.data);
      } catch (error) {
        console.error('Error checking cancellation limit:', error);
        // Continue anyway, will show error if limit exceeded when trying to cancel
      }
    }
    
    setShowCancelModal(true);
  };

  const handleRescheduleAppointment = async (appointment) => {
    setSelectedAppointment(appointment);
    
    // Check reschedule limit for parents
    if (user?.role === 'parent') {
      try {
        const response = await api.get('/appointments/reschedule-limit');
        setRescheduleLimit(response.data);
        
        // Fetch available reschedule options
        setLoadingRescheduleOptions(true);
        const optionsResponse = await api.get(`/appointments/${appointment._id}/reschedule-options`);
        setRescheduleOptions(optionsResponse.data.options || []);
      } catch (error) {
        console.error('Error checking reschedule limit or fetching options:', error);
        toast.error('Failed to load reschedule options');
      } finally {
        setLoadingRescheduleOptions(false);
      }
    } else if (user?.role === 'admin') {
      // For admin only: Keep original date/time picker form
      setEditFormData({
        scheduledDate: moment(appointment.scheduledDate).format('YYYY-MM-DD'),
        startTime: appointment.startTime,
        endTime: appointment.endTime,
        subject: appointment.subject || '',
        notes: appointment.notes || ''
      });
    }
    
    // Only show reschedule modal for admin and parent
    if (user?.role === 'admin' || user?.role === 'parent') {
      setShowRescheduleModal(true);
    }
  };

  const confirmCancelAppointment = async () => {
    if (!selectedAppointment) return;

    try {
      const response = await api.put(`/appointments/${selectedAppointment._id}/cancel`);
      toast.success(response.data?.message || 'Appointment cancelled successfully');
      setShowCancelModal(false);
      setSelectedAppointment(null);
      setCancellationLimit(null);
      fetchAppointments();
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      const errorMessage = error.response?.data?.message || 'Failed to cancel appointment';
      
      // Show specific message for monthly limit reached
      if (error.response?.data?.limitReached) {
        // Update cancellation limit state if limit was reached
        setCancellationLimit({
          canCancel: false,
          currentCount: error.response.data.currentCount,
          limit: error.response.data.limit,
          month: error.response.data.month
        });
        toast.error(errorMessage, {
          duration: 5000 // Show longer for important messages
        });
      } else {
        toast.error(errorMessage);
      }
    }
  };

  const confirmRescheduleAppointment = async () => {
    if (!selectedAppointment) return;

    // Check if limit exceeded and we need to submit a request instead (for parents)
    if (user?.role === 'parent' && rescheduleLimit && !rescheduleLimit.canReschedule) {
      // Submit reschedule request
      if (!rescheduleReason || rescheduleReason.length < 10) {
        toast.error('Please provide a reason (at least 10 characters) for the reschedule request');
        return;
      }

      try {
        const newDate = selectedRescheduleOption ? selectedRescheduleOption.dateString : undefined;
        const newStartTime = selectedRescheduleOption ? selectedRescheduleOption.startTime : undefined;
        const newEndTime = selectedRescheduleOption ? selectedRescheduleOption.endTime : undefined;

        await api.post(`/appointments/${selectedAppointment._id}/reschedule-request`, {
          reason: rescheduleReason,
          newDate: newDate,
          newStartTime: newStartTime,
          newEndTime: newEndTime
        });

        toast.success('Reschedule request submitted. Admin will be notified.');
        setShowRescheduleModal(false);
        setSelectedAppointment(null);
        setRescheduleReason('');
        setRescheduleLimit(null);
        setRescheduleOptions([]);
        setSelectedRescheduleOption(null);
        fetchAppointments();
      } catch (error) {
        console.error('Error submitting reschedule request:', error);
        toast.error(error.response?.data?.message || 'Failed to submit reschedule request');
      }
      return;
    }

    // For parents: Direct reschedule (within limit) - must select from available appointments
    if (user?.role === 'parent') {
      if (!selectedRescheduleOption) {
        toast.error('Please select an available appointment time');
        return;
      }

      try {
        await api.put(`/appointments/${selectedAppointment._id}/reschedule`, {
          scheduleId: selectedRescheduleOption.scheduleId,
          dateString: selectedRescheduleOption.dateString,
          startTime: selectedRescheduleOption.startTime,
          endTime: selectedRescheduleOption.endTime
        });
        toast.success('Appointment rescheduled successfully');
        setShowRescheduleModal(false);
        setSelectedAppointment(null);
        setRescheduleLimit(null);
        setRescheduleOptions([]);
        setSelectedRescheduleOption(null);
        fetchAppointments();
      } catch (error) {
        console.error('Error rescheduling appointment:', error);
        if (error.response?.data?.limitExceeded) {
          // If limit was exceeded during reschedule, update state and show request form
          setRescheduleLimit({
            canReschedule: false,
            currentCount: error.response.data.currentCount,
            limit: error.response.data.limit,
            quarter: error.response.data.quarter
          });
          toast.error(error.response.data.message);
        } else {
          toast.error(error.response?.data?.message || 'Failed to reschedule appointment');
        }
      }
      return;
    }

    // For admin only: Use original date/time picker form
    if (user?.role === 'admin') {
      try {
        await api.put(`/appointments/${selectedAppointment._id}/reschedule`, {
          scheduledDate: editFormData.scheduledDate,
          startTime: editFormData.startTime,
          endTime: editFormData.endTime
        });
        toast.success('Appointment rescheduled successfully');
        setShowRescheduleModal(false);
        setSelectedAppointment(null);
        fetchAppointments();
      } catch (error) {
        console.error('Error rescheduling appointment:', error);
        toast.error(error.response?.data?.message || 'Failed to reschedule appointment');
      }
    }
  };

  const handleInputChange = (e, formType) => {
    const { name, value } = e.target;
    if (formType === 'edit') {
      setEditFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      available: { class: 'status-available', text: 'Available' },
      booked: { class: 'status-booked', text: 'Booked' },
      scheduled: { class: 'status-scheduled', text: 'Scheduled' },
      confirmed: { class: 'status-confirmed', text: 'Confirmed' },
      'in-progress': { class: 'status-in-progress', text: 'In Progress' },
      completed: { class: 'status-completed', text: 'Completed' },
      cancelled: { class: 'status-cancelled', text: 'Cancelled' },
      rescheduled: { class: 'status-rescheduled', text: 'Rescheduled' }
    };

    const config = statusConfig[status] || statusConfig.available;
    return <span className={config.class}>{config.text}</span>;
  };

  // Convert 24-hour time (HH:MM) to 12-hour format (h:mm AM/PM)
  const formatTimeTo12Hour = (time24) => {
    if (!time24) return 'N/A';
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const formatDateTime = (date, time) => {
    return moment(date).format('MMM Do, YYYY') + ' at ' + formatTimeTo12Hour(time);
  };

  const canManageAppointment = (appointment) => {
    // Only admins can manage appointments, teachers can only view
    if (user?.role === 'admin') return true;
    return false;
  };

  const getRoleSpecificUsers = (role) => {
    return users.filter(u => u.role === role && u.isActive);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {user?.role === 'admin' && 'Appointments Management'}
            {user?.role === 'teacher' && 'My Assigned Appointments'}
            {user?.role === 'parent' && 'My Booked Appointments'}
          </h1>
          <p className="text-gray-600">
            {user?.role === 'admin' && 'View and manage all booked tutoring sessions from parents'}
            {user?.role === 'teacher' && 'View your assigned tutoring sessions'}
            {user?.role === 'parent' && 'View your children\'s booked tutoring sessions. To book new appointments, use the Calendar tab.'}
          </p>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search appointments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-10"
              />
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input"
            >
              <option value="">All Statuses</option>
              <option value="booked">Booked</option>
              <option value="rescheduled">Rescheduled</option>
              <option value="cancelled">Cancelled</option>
            </select>

            {/* Date Filter */}
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="input"
            />

            {/* Refresh Button */}
            <button
              onClick={fetchAppointments}
              className="btn btn-secondary"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Appointments Table */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">Appointments</h3>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="loading-spinner h-8 w-8"></div>
            </div>
          ) : appointments.length === 0 ? (
            <div className="text-center py-8">
              <Calendar className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No appointments found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm || statusFilter || dateFilter 
                  ? 'Try adjusting your search criteria or filters.' 
                  : user?.role === 'admin' 
                    ? 'Get started by creating a new appointment.'
                    : user?.role === 'teacher'
                      ? 'You have no assigned appointments yet.'
                      : 'No available appointments at the moment.'
                }
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table min-w-full">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">Date & Time</th>
                    <th className="table-header-cell">Student</th>
                    <th className="table-header-cell">Teacher</th>
                    <th className="table-header-cell">Subject</th>
                    <th className="table-header-cell">Time Booked</th>
                    <th className="table-header-cell">Booked By</th>
                    <th className="table-header-cell">Status</th>
                    <th className="table-header-cell">Actions</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {appointments.map((appointment) => (
                    <tr key={appointment._id} className="table-row">
                      <td className="table-cell">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {formatDateTime(appointment.scheduledDate, appointment.startTime)}
                          </div>
                          <div className="text-sm text-gray-500">
                            {formatTimeTo12Hour(appointment.startTime)} - {formatTimeTo12Hour(appointment.endTime)}
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm font-medium text-gray-900">
                          {appointment.student 
                            ? `${appointment.student.firstName || ''} ${appointment.student.lastName || ''}`.trim() || 'N/A'
                            : 'No Student Assigned'
                          }
                        </div>
                        {appointment.student?.grade && (
                          <div className="text-xs text-gray-500">
                            Grade {appointment.student.grade}
                          </div>
                        )}
                      </td>
                      <td className="table-cell">
                        <div className="text-sm font-medium text-gray-900">
                          {appointment.teacher?.firstName} {appointment.teacher?.lastName}
                        </div>
                      </td>
                      <td className="table-cell">
                        <span className={`badge text-xs ${
                          appointment.primarySubject === 'abacus' ? 'badge-warning' :
                          appointment.primarySubject === 'math' ? 'badge-primary' :
                          appointment.primarySubject === 'english' ? 'badge-success' :
                          appointment.primarySubject === 'coding' ? 'badge-danger' :
                          'badge-secondary'
                        }`}>
                          {appointment.subject}
                        </span>
                      </td>
                      <td className="table-cell">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {appointment.bookedAt ? moment(appointment.bookedAt).format('MMM Do, YYYY') : 'N/A'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {appointment.bookedAt ? moment(appointment.bookedAt).format('h:mm A') : 'Not booked'}
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm font-medium text-gray-900">
                          {appointment.bookedBy?.firstName} {appointment.bookedBy?.lastName}
                        </div>
                      </td>
                      <td className="table-cell">
                        {getStatusBadge(appointment.status)}
                      </td>
                      <td className="table-cell">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleViewAppointment(appointment)}
                            className="btn btn-sm btn-secondary"
                            title="View Appointment"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          
                          {/* Admin actions only */}
                          {canManageAppointment(appointment) && (
                            <>
                              <button
                                onClick={() => handleEditAppointment(appointment)}
                                className="btn btn-sm btn-primary"
                                title="Edit Appointment"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              {!['cancelled', 'completed'].includes(appointment.status) && (
                                <button
                                  onClick={() => handleCancelAppointment(appointment)}
                                  className="btn btn-sm btn-danger"
                                  title="Cancel Appointment"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              )}
                            </>
                          )}
                          
                          {/* Parent actions - booking only */}
                          {user?.role === 'parent' && appointment.status === 'available' && (
                            <button
                              onClick={() => handleBookAppointment(appointment._id)}
                              className="btn btn-sm btn-success"
                              title="Book Appointment"
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                          )}
                          
                          {/* Parent actions - cancel and reschedule */}
                          {user?.role === 'parent' && ['booked', 'scheduled', 'confirmed'].includes(appointment.status) && (
                            <>
                              <button
                                onClick={() => handleRescheduleAppointment(appointment)}
                                className="btn btn-sm btn-warning"
                                title="Reschedule Appointment"
                              >
                                <Clock className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleCancelAppointment(appointment)}
                                className="btn btn-sm btn-danger"
                                title="Cancel Appointment"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </>
                          )}
                          
                          {/* Teacher actions - view only (no management) */}
                          {user?.role === 'teacher' && (
                            <span className="text-sm text-gray-500 italic">
                              View Only
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Page {currentPage} of {totalPages}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="btn btn-secondary btn-sm disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="btn btn-secondary btn-sm disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* View Appointment Modal */}
      {showViewModal && selectedAppointment && (
        <div className="modal-overlay" onClick={() => setShowViewModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="text-lg font-medium text-gray-900">Appointment Details</h3>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-gray-400 hover:text-gray-600"
                title="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="modal-body">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Date & Time</label>
                  <p className="text-sm text-gray-900">
                    {formatDateTime(selectedAppointment.scheduledDate, selectedAppointment.startTime)}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Duration</label>
                  <p className="text-sm text-gray-900">
                    {formatTimeTo12Hour(selectedAppointment.startTime)} - {formatTimeTo12Hour(selectedAppointment.endTime)}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Subject</label>
                  <p className="text-sm text-gray-900">{selectedAppointment.subject}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Student</label>
                  <p className="text-sm text-gray-900">
                    {selectedAppointment.student 
                      ? `${selectedAppointment.student.firstName || ''} ${selectedAppointment.student.lastName || ''}`.trim() || 'N/A'
                      : 'No Student Assigned'
                    }
                    {selectedAppointment.student?.grade && (
                      <span className="text-gray-500 ml-2">(Grade {selectedAppointment.student.grade})</span>
                    )}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Teacher</label>
                  <p className="text-sm text-gray-900">
                    {selectedAppointment.teacher?.firstName} {selectedAppointment.teacher?.lastName}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <p className="text-sm text-gray-900">{getStatusBadge(selectedAppointment.status)}</p>
                </div>
                {selectedAppointment.bookedBy && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Booked By</label>
                    <p className="text-sm text-gray-900">
                      {selectedAppointment.bookedBy.firstName} {selectedAppointment.bookedBy.lastName}
                    </p>
                    <p className="text-xs text-gray-500">{selectedAppointment.bookedBy.email}</p>
                  </div>
                )}
                {selectedAppointment.bookedAt && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Booking Time</label>
                    <p className="text-sm text-gray-900">
                      {moment(selectedAppointment.bookedAt).format('MMM Do, YYYY [at] h:mm A')}
                    </p>
                  </div>
                )}
                {selectedAppointment.notes && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Notes</label>
                    <p className="text-sm text-gray-900">{selectedAppointment.notes}</p>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowViewModal(false)}
                className="btn btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Appointment Modal - Admin only */}
      {showEditModal && selectedAppointment && user?.role === 'admin' && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="text-lg font-medium text-gray-900">Edit Appointment</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600"
                title="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleUpdateAppointment} className="modal-body">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Student
                  </label>
                  <select
                    name="student"
                    value={editFormData.student}
                    onChange={(e) => handleInputChange(e, 'edit')}
                    className="input w-full"
                    required
                  >
                    <option value="">Select Student</option>
                    {getRoleSpecificUsers('parent').map(user => (
                      <option key={user._id} value={user._id}>
                        {user.firstName} {user.lastName}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Teacher
                  </label>
                  <select
                    name="teacher"
                    value={editFormData.teacher}
                    onChange={(e) => handleInputChange(e, 'edit')}
                    className="input w-full"
                    required
                  >
                    <option value="">Select Teacher</option>
                    {getRoleSpecificUsers('teacher').map(user => (
                      <option key={user._id} value={user._id}>
                        {user.firstName} {user.lastName}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subject
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={editFormData.subject}
                    onChange={(e) => handleInputChange(e, 'edit')}
                    className="input w-full"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date
                  </label>
                  <input
                    type="date"
                    name="scheduledDate"
                    value={editFormData.scheduledDate}
                    onChange={(e) => handleInputChange(e, 'edit')}
                    className="input w-full"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Time
                  </label>
                  <input
                    type="time"
                    name="startTime"
                    value={editFormData.startTime}
                    onChange={(e) => handleInputChange(e, 'edit')}
                    className="input w-full"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Time
                  </label>
                  <input
                    type="time"
                    name="endTime"
                    value={editFormData.endTime}
                    onChange={(e) => handleInputChange(e, 'edit')}
                    className="input w-full"
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    name="notes"
                    value={editFormData.notes}
                    onChange={(e) => handleInputChange(e, 'edit')}
                    className="input w-full"
                    rows="3"
                  />
                </div>
              </div>
            </form>
            <div className="modal-footer">
              <button
                onClick={() => setShowEditModal(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateAppointment}
                className="btn btn-primary"
              >
                Update Appointment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Appointment Modal - Admin and Parent only */}
      {showCancelModal && selectedAppointment && (user?.role === 'admin' || user?.role === 'parent') && (
        <div className="modal-overlay" onClick={() => setShowCancelModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="text-lg font-medium text-gray-900">Cancel Appointment</h3>
              <button
                onClick={() => setShowCancelModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="modal-body">
              {/* Show cancellation limit status for parents */}
              {user?.role === 'parent' && cancellationLimit && (
                <div className={`border rounded-lg p-4 mb-4 ${
                  cancellationLimit.canCancel 
                    ? 'bg-blue-50 border-blue-200' 
                    : 'bg-yellow-50 border-yellow-200'
                }`}>
                  <div className="flex items-start">
                    <AlertTriangle className={`h-5 w-5 mt-0.5 mr-2 ${
                      cancellationLimit.canCancel 
                        ? 'text-blue-600' 
                        : 'text-yellow-600'
                    }`} />
                    <div>
                      <p className={`text-sm font-medium ${
                        cancellationLimit.canCancel 
                          ? 'text-blue-800' 
                          : 'text-yellow-800'
                      }`}>
                        {cancellationLimit.canCancel 
                          ? `Cancellation Token: ${cancellationLimit.currentCount} of ${cancellationLimit.limit} used (${cancellationLimit.month})`
                          : 'Cancellation Limit Reached - Contact Admin Required'
                        }
                      </p>
                      <p className={`text-sm mt-1 ${
                        cancellationLimit.canCancel 
                          ? 'text-blue-700' 
                          : 'text-yellow-700'
                      }`}>
                        {cancellationLimit.canCancel 
                          ? `You have ${cancellationLimit.limit - cancellationLimit.currentCount} cancellation token${cancellationLimit.limit - cancellationLimit.currentCount !== 1 ? 's' : ''} remaining this month.`
                          : `You have used all ${cancellationLimit.limit} cancellation token${cancellationLimit.limit !== 1 ? 's' : ''} for this month (${cancellationLimit.month}). You must contact the admin to cancel additional appointments.`
                        }
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-center space-x-3 mb-4">
                <AlertTriangle className="h-8 w-8 text-red-500" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Are you sure you want to cancel this appointment?</p>
                  <p className="text-sm text-gray-500">This action cannot be undone.</p>
                </div>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Appointment Details:</h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <p><strong>Date:</strong> {formatDateTime(selectedAppointment.scheduledDate, selectedAppointment.startTime)}</p>
                  <p><strong>Time:</strong> {formatTimeTo12Hour(selectedAppointment.startTime)} - {formatTimeTo12Hour(selectedAppointment.endTime)}</p>
                  <p><strong>Subject:</strong> {selectedAppointment.subject}</p>
                  <p><strong>Teacher:</strong> {selectedAppointment.teacher?.firstName} {selectedAppointment.teacher?.lastName}</p>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowCancelModal(false)}
                className="btn btn-secondary"
              >
                Keep Appointment
              </button>
              <button
                onClick={confirmCancelAppointment}
                disabled={user?.role === 'parent' && cancellationLimit && !cancellationLimit.canCancel}
                className="btn btn-danger disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel Appointment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reschedule Appointment Modal - Admin and Parent only */}
      {showRescheduleModal && selectedAppointment && (user?.role === 'admin' || user?.role === 'parent') && (
        <div className="modal-overlay" onClick={() => setShowRescheduleModal(false)}>
          <div className="modal max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="text-lg font-medium text-gray-900">Reschedule Appointment</h3>
              <button
                onClick={() => {
                  setShowRescheduleModal(false);
                  setSelectedAppointment(null);
                  setRescheduleOptions([]);
                  setSelectedRescheduleOption(null);
                  setRescheduleReason('');
                  setRescheduleLimit(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="modal-body">
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Current Appointment:</h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <p><strong>Date:</strong> {formatDateTime(selectedAppointment.scheduledDate, selectedAppointment.startTime)}</p>
                  <p><strong>Time:</strong> {formatTimeTo12Hour(selectedAppointment.startTime)} - {formatTimeTo12Hour(selectedAppointment.endTime)}</p>
                  <p><strong>Subject:</strong> {selectedAppointment.subject}</p>
                  <p><strong>Teacher:</strong> {selectedAppointment.teacher?.firstName} {selectedAppointment.teacher?.lastName}</p>
                </div>
              </div>

              {/* Show reschedule limit status */}
              {user?.role === 'parent' && rescheduleLimit && (
                <div className={`border rounded-lg p-4 mb-4 ${
                  rescheduleLimit.canReschedule 
                    ? 'bg-blue-50 border-blue-200' 
                    : 'bg-yellow-50 border-yellow-200'
                }`}>
                  <div className="flex items-start">
                    <AlertTriangle className={`h-5 w-5 mt-0.5 mr-2 ${
                      rescheduleLimit.canReschedule 
                        ? 'text-blue-600' 
                        : 'text-yellow-600'
                    }`} />
                    <div>
                      <p className={`text-sm font-medium ${
                        rescheduleLimit.canReschedule 
                          ? 'text-blue-800' 
                          : 'text-yellow-800'
                      }`}>
                        {rescheduleLimit.canReschedule 
                          ? `Reschedule Tokens: ${rescheduleLimit.currentCount} of ${rescheduleLimit.limit} used (${rescheduleLimit.quarter})`
                          : 'Reschedule Limit Reached - Contact Admin Required'
                        }
                      </p>
                      <p className={`text-sm mt-1 ${
                        rescheduleLimit.canReschedule 
                          ? 'text-blue-700' 
                          : 'text-yellow-700'
                      }`}>
                        {rescheduleLimit.canReschedule 
                          ? `You have ${rescheduleLimit.limit - rescheduleLimit.currentCount} reschedule token${rescheduleLimit.limit - rescheduleLimit.currentCount !== 1 ? 's' : ''} remaining this quarter.`
                          : `You have used all ${rescheduleLimit.limit} reschedule tokens for this quarter (${rescheduleLimit.quarter}). You must contact the admin to request a reschedule. Please submit a reschedule request below.`
                        }
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* For parents: Show available appointments list */}
              {user?.role === 'parent' ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {rescheduleLimit && !rescheduleLimit.canReschedule 
                        ? 'Select Preferred Appointment (Optional - Admin will review)' 
                        : 'Select New Appointment Time from Available Appointments *'}
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      Choose from existing available appointment slots that match your student's class ({selectedAppointment?.primarySubject || 'N/A'}). Only appointments within the next 2 weeks are shown.
                    </p>
                    
                    {loadingRescheduleOptions ? (
                      <div className="text-center py-8">
                        <p className="text-sm text-gray-500">Loading available appointments...</p>
                      </div>
                    ) : rescheduleOptions.length === 0 ? (
                      <div className="text-center py-8 border border-gray-200 rounded-lg">
                        <p className="text-sm text-gray-500">No available appointments found for the next 2 weeks.</p>
                        <p className="text-xs text-gray-400 mt-1">Please contact admin for assistance.</p>
                      </div>
                    ) : (
                      <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
                        {rescheduleOptions.map((option, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => setSelectedRescheduleOption(option)}
                            className={`w-full text-left p-3 border-b border-gray-200 last:border-b-0 hover:bg-gray-50 transition-colors ${
                              selectedRescheduleOption?.scheduleId === option.scheduleId &&
                              selectedRescheduleOption?.dateString === option.dateString &&
                              selectedRescheduleOption?.startTime === option.startTime
                                ? 'bg-blue-50 border-l-4 border-l-blue-500' 
                                : ''
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {new Date(option.date).toLocaleDateString('en-US', { 
                                    weekday: 'short', 
                                    month: 'short', 
                                    day: 'numeric' 
                                  })}
                                </p>
                                <p className="text-sm text-gray-600">
                                  {formatTimeTo12Hour(option.startTime)} - {formatTimeTo12Hour(option.endTime)}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                  {option.teacher.firstName} {option.teacher.lastName}
                                </p>
                              </div>
                              <div className="text-right">
                                <span className="text-xs text-green-600 font-medium">
                                  {option.availableSpots} spot{option.availableSpots !== 1 ? 's' : ''} available
                                </span>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Reason field for reschedule requests (required when limit exceeded) */}
                  {rescheduleLimit && !rescheduleLimit.canReschedule && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Reason for Reschedule Request *
                      </label>
                      <p className="text-xs text-gray-500 mb-2">
                        Since you have used all your reschedule tokens, you must contact the admin. Please provide a detailed reason for your reschedule request.
                      </p>
                      <textarea
                        value={rescheduleReason}
                        onChange={(e) => setRescheduleReason(e.target.value)}
                        placeholder="Please explain why you need to reschedule this appointment. The admin will review your request and contact you..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        rows={4}
                        required
                        minLength={10}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Minimum 10 characters required. Admin will be notified and will review your request.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                /* For admin only: Keep original date/time picker form */
                user?.role === 'admin' && (
                  <form onSubmit={(e) => e.preventDefault()}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          New Date *
                        </label>
                        <input
                          type="date"
                          name="scheduledDate"
                          value={editFormData.scheduledDate}
                          onChange={(e) => handleInputChange(e, 'edit')}
                          className="input w-full"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Start Time *
                        </label>
                        <input
                          type="time"
                          name="startTime"
                          value={editFormData.startTime}
                          onChange={(e) => handleInputChange(e, 'edit')}
                          className="input w-full"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          End Time *
                        </label>
                        <input
                          type="time"
                          name="endTime"
                          value={editFormData.endTime}
                          onChange={(e) => handleInputChange(e, 'edit')}
                          className="input w-full"
                          required
                        />
                      </div>
                    </div>
                  </form>
                )
              )}

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowRescheduleModal(false);
                    setSelectedAppointment(null);
                    setRescheduleOptions([]);
                    setSelectedRescheduleOption(null);
                    setRescheduleReason('');
                    setRescheduleLimit(null);
                  }}
                  className="flex-1 px-4 py-2 text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmRescheduleAppointment}
                  disabled={
                    user?.role === 'parent' && (
                      (rescheduleLimit?.canReschedule && !selectedRescheduleOption) ||
                      (!rescheduleLimit?.canReschedule && !rescheduleReason)
                    )
                  }
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {user?.role === 'parent' && rescheduleLimit && !rescheduleLimit.canReschedule 
                    ? 'Submit Request' 
                    : 'Reschedule Appointment'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Appointments;